import { ReactElement } from 'react';
import { Checkbox, Button, styled } from '@mui/material';
import {
  IDeaInfo,
  ISaveRequestOutput,
  ValueLabel,
  ExpandButton,
  IRequestDetails,
  sailpointApproveRequestUrl,
  formatDate,
  isRequestNotCancellable,
  variables,
} from '../common';
import clsx from 'clsx';
import {
  IMoreRequestData,
  RequestStatusType,
  isRequestStatusTypeForApproval,
} from '.';
import {
  map as lmap,
  compose as lcompose,
  find as lfind,
  isEmpty as lisEmpty,
} from 'lodash/fp';
import { path, not } from 'rambda';
import { left, right, fold } from 'fp-ts/Either';
import { flow } from 'fp-ts/function';

export const renderStack =
  ({ requestStatusType }: { requestStatusType: RequestStatusType }) =>
  (s: any) =>
  (properties: ValueLabel<keyof ISaveRequestOutput>[]) =>
  ({
    disableOperationsHelpDsk,
    expandRequestValue,
    toggleExpandRequest,
    now,
    selectedRequests,
    toggleRequestSelection,
    confirmCancelRequests,
  }: {
    disableOperationsHelpDsk: boolean;
    expandRequestValue: (name: string) => boolean;
    toggleExpandRequest: (name: string) => void;
    now?: Date;
    selectedRequests?: ISaveRequestOutput[];
    toggleRequestSelection?: (payload: ISaveRequestOutput) => void;
    confirmCancelRequests?: () => void;
  }) =>
  (moreRequestData: IMoreRequestData[]) =>
    lcompose(
      fold(
        () => <article>Empty</article>,

        requests => (
          <Articl className={s.wrapper}>
            {lmap<ISaveRequestOutput, ReactElement>(a => (
              <Article
                key={a.requestId}
                className={clsx(s.wrapper, s.itemWrapper)}>
                {lmap<ValueLabel<keyof ISaveRequestOutput>, ReactElement>(
                  ({ value, label }) => (
                    <Line key={value}>
                      <Label>{label}:</Label>
                      <Line>
                        <>
                          {value === 'deaInfos'
                            ? lmap<IDeaInfo, ReactElement>(b => (
                                <span key={`${b.deaNum}-deaNum`}>
                                  {b.deaNum}{' '}
                                </span>
                              ))(a[value])
                            : value === 'updatedAt'
                            ? formatDate(a[value])
                            : value === 'beneficiaryInfo'
                            ? [
                                <div key="beneficiaryInfo-1">{`${
                                  a.beneficiaryFirstName || a.firstName
                                } ${
                                  a.beneficiaryLastName || a.lastName
                                }`}</div>,
                                <div key="beneficiaryInfo-2">{`${a.beneficiaryEmail}`}</div>,
                              ]
                            : value === 'requestorEmail' &&
                              a.requestorEmail === 'RA Group'
                            ? 'Registration Authority'
                            : value === 'requestorEmail' &&
                              a.requestorEmail === 'DI Group'
                            ? 'Diversion Investigators'
                            : value === 'requestorEmail' && 
                              a.requestorEmail === 'Drug Enforcement Administrator'
                            ? 'Drug Enforcement Administrator'
                            : value === 'requestorEmail'
                            ? [
                                <div key="requestorEmail-1">{`${
                                  a.requestorFirstName || ''
                                } ${a.requestorLastName || ''}`}</div>,
                                <div key="requestorEmail-2">{`${a.requestorEmail}`}</div>,
                              ]
                            : a[value]}
                        </>
                      </Line>
                    </Line>
                  )
                )(properties)}

                {isRequestStatusTypeForApproval(requestStatusType) && (
                  <span className={s.line}>
                    {disableOperationsHelpDsk ? null : (
                      <a
                        href={sailpointApproveRequestUrl(a.sailpointCaseId)}
                        target="_blank"
                        rel="noreferrer">
                        Review/Approve/Reject Link
                      </a>
                    )}
                  </span>
                )}

                {(requestStatusType === 'SelfRequestStatus' ||
                  requestStatusType === 'MemberRequestStatus') && (
                  <Checkbox
                    checked={flow(
                      lfind<ISaveRequestOutput>({
                        requestId: a.requestId,
                      }),
                      lisEmpty,
                      not
                    )(selectedRequests)}
                    disabled={isRequestNotCancellable(a)}
                    color="primary"
                    onClick={toggleRequestSelection?.bind(null, a)}
                  />
                )}

                <ExpandButton
                  value={expandRequestValue(String(a.requestId))}
                  matchesGridBreakpoint={false}
                  onClick={() => toggleExpandRequest(String(a.requestId))}
                />

                {expandRequestValue(String(a.requestId)) ? (
                  <span>
                    DEA Registration Numbers:{' '}
                    {showDetails(
                      lcompose(
                        path<IRequestDetails>('details'),
                        lfind<IMoreRequestData>({ requestId: a.requestId })
                      )(moreRequestData)
                    )('deaInfos')}
                  </span>
                ) : (
                  void 0
                )}
              </Article>
            ))(requests)}

            <Button
              onClick={confirmCancelRequests}
              disabled={lisEmpty(selectedRequests) || disableOperationsHelpDsk}
              variant="contained">
              Cancel Request
            </Button>

            {/* <pre>{JSON.stringify(selectedRequests, null, 2)}</pre> */}
          </Articl>
        )
      ),
      (requests: ISaveRequestOutput[]) =>
        requests.length === 0 ? left(requests) : right(requests)
    );

const showDetails =
  (a: IRequestDetails | undefined) =>
  (value: keyof IRequestDetails): string | ReactElement[] => {
    if (a === undefined) {
      return '';
    }

    if (value === 'deaInfos') {
      return lmap<IDeaInfo, ReactElement>(b => (
        <span key={`${b.deaNum}-deaNum`}>{b.deaNum} </span>
      ))(a[value]);
    }

    if (value === 'additionalInfo' && a.status === 'FAILURE') {
      return 'Your account has not been fully synchronized. Please create CSOS Certificate request in 2-3 hours.';
    }

    return a[value] as string;
  };

const Articl = styled('article')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  '&>*+*': {
    marginTop: '0.5rem',
  },
}));

const Article = styled('article')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  '&>*+*': {
    marginTop: '0.5rem',
  },

  paddingBottom: '0.5rem',
  borderBottom: variables.cardBorder,
}));

const Label = styled('span')(({ theme }) => ({ fontWeight: 700 }));

const Line = styled('span')(({ theme }) => ({
  '&>*+*': {
    marginLeft: '1rem',
  },
}));
